/* eslint-disable no-console */
/* eslint-disable unicorn/prefer-module */
import React, { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import {
  TIPOS_ACTIVO,
  DESTINO_TIPOS,
  getVidaUtilPorTipo,
  esIntangiblePorTipo,
} from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { useCurrency } from "../../utils";
import { getActivos, crearActivo, actualizarActivo, eliminarActivo } from "../../utils/activos";
import { client } from "../../utils/supabase";
import { Header } from "../Header";
import styles from "./GestionActivos.module.scss";

// =====================================================
// CONSTANTES
// =====================================================

const ESTADOS_ACTIVO = [
  { value: "activo", label: "Activo" },
  { value: "dado_de_baja", label: "Dado de baja" },
  { value: "vendido", label: "Vendido" },
  { value: "en_reparacion", label: "En reparación" },
  { value: "planificado", label: "Planificado" },
];

const MONEDAS_POR_PAIS = {
  Chile: ["CLP", "USD", "UF"],
  Perú: ["PEN", "USD"],
};

// =====================================================
// HELPERS
// =====================================================

const formatearTexto = (texto) => {
  if (!texto || typeof texto !== "string") return "";
  const limpio = texto.trim();
  if (limpio.length === 0) return "";
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
};

const parseFechaLocal = (fechaStr) => {
  if (!fechaStr) return null;
  const [year, month, day] = fechaStr.split("-");
  return new Date(year, month - 1, day);
};

const formatFechaToISO = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Calcular depreciación
const calcularDepreciacion = (costoTotal, vidaUtilMeses, fechaCompra) => {
  const depreciacionMensual = costoTotal / vidaUtilMeses;

  const fechaCompraDate = new Date(fechaCompra);
  const hoy = new Date();

  // Meses transcurridos
  const mesesTranscurridos =
    (hoy.getFullYear() - fechaCompraDate.getFullYear()) * 12 + (hoy.getMonth() - fechaCompraDate.getMonth());

  const mesesEfectivos = Math.max(0, Math.min(mesesTranscurridos, vidaUtilMeses));
  const depreciacionAcumulada = depreciacionMensual * mesesEfectivos;
  const valorLibros = Math.max(0, costoTotal - depreciacionAcumulada);
  const mesesRestantes = Math.max(0, vidaUtilMeses - mesesEfectivos);

  return {
    depreciacionMensual,
    depreciacionAcumulada,
    valorLibros,
    mesesTranscurridos: mesesEfectivos,
    mesesRestantes,
    porcentajeDepreciado: (mesesEfectivos / vidaUtilMeses) * 100,
  };
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const GestionActivos = () => {
  const { pais } = useAuthContext();
  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : null;
  const { loadingRates, formatAmount } = useCurrency();

  // Estados principales
  const [activos, setActivos] = useState([]);
  const [edificios, setEdificios] = useState({});
  const [proyectosCarteleria, setProyectosCarteleria] = useState({});
  const [empleados, setEmpleados] = useState({});
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Formulario
  const [formData, setFormData] = useState({
    fecha_compra: null,
    tipo_activo: "",
    descripcion: "",
    numero_serie: "",
    cantidad: 1,
    costo_unitario: "",
    moneda: normalizedPais === "Chile" ? "CLP" : "PEN",
    estado: "activo",
    destino_tipo: "",
    destino_id: "",
    destino_empleado_id: "",
    numero_factura: "",
    proyecto_nombre: "",
    fecha_planificada: null,
    fecha_activacion: null,
    categoria_contable: "",
    proveedor_id: null,
    factura_sii_recibida_id: null,
    metodo_depreciacion: "lineal",
  });

  // =====================================================
  // FETCH DATA
  // =====================================================

  const fetchData = async () => {
    if (!normalizedPais) {
      setError("No se ha definido el país del usuario");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [activosData, edificiosRes, carteleriaRes, proveedoresRes, empleadosRes] = await Promise.all([
        getActivos(normalizedPais),
        client
          .from("edificios")
          .select("id, edificio")
          .eq("pais", normalizedPais)
          .order("edificio", { ascending: true }),
        client
          .from("negocios_carteleria")
          .select("id, nombre_proyecto, referencia_id")
          .eq("pais", normalizedPais)
          .eq("eliminada", false)
          .order("id", { ascending: false }),
        client
          .from("proveedores")
          .select("id, nombre")
          .eq("pais", normalizedPais)
          .eq("habilitado", true)
          .order("nombre"),
        client
          .from("empleados")
          .select("id, nombre, apellido, egreso_fecha")
          .eq("pais", normalizedPais)
          .is("egreso_fecha", null)
          .order("nombre", { ascending: true }),
      ]);

      if (edificiosRes.error) throw new Error(`Edificios: ${edificiosRes.error.message}`);
      if (carteleriaRes.error) throw new Error(`Cartelería: ${carteleriaRes.error.message}`);

      setActivos(activosData || []);
      setProveedores(proveedoresRes.data || []);
      setEdificios(edificiosRes.data.reduce((acc, e) => ({ ...acc, [e.id]: e.edificio }), {}));
      setProyectosCarteleria(
        carteleriaRes.data.reduce(
          (acc, p) => ({
            ...acc,
            [p.id]: p.nombre_proyecto || `Proyecto #${p.id}`,
          }),
          {}
        )
      );
      setEmpleados(
        (empleadosRes.data || []).reduce(
          (acc, emp) => ({ ...acc, [emp.id]: `${emp.nombre} ${emp.apellido || ""}`.trim() }),
          {}
        )
      );
    } catch (err) {
      console.error("Error fetching data:", err.message);
      setError(err.message);
      toast.error("Error al cargar los datos: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [normalizedPais]);

  // =====================================================
  // HANDLERS
  // =====================================================

  const resetForm = () => {
    setFormData({
      fecha_compra: null,
      tipo_activo: "",
      descripcion: "",
      numero_serie: "",
      cantidad: 1,
      costo_unitario: "",
      moneda: normalizedPais === "Chile" ? "CLP" : "PEN",
      estado: "activo",
      destino_tipo: "",
      destino_id: "",
      destino_empleado_id: "",
      numero_factura: "",
      proyecto_nombre: "",
      fecha_planificada: null,
      fecha_activacion: null,
      categoria_contable: "",
      proveedor_id: null,
      factura_sii_recibida_id: null,
      metodo_depreciacion: "lineal",
    });
    setEditingId(null);
  };

  const openModal = (activo = null) => {
    if (activo) {
      setFormData({
        fecha_compra: activo.fecha_compra ? parseFechaLocal(activo.fecha_compra) : null,
        tipo_activo: activo.tipo_activo || "",
        descripcion: activo.descripcion || "",
        numero_serie: activo.numero_serie || "",
        cantidad: activo.cantidad || 1,
        costo_unitario: activo.costo_unitario || "",
        moneda: activo.moneda || (normalizedPais === "Chile" ? "CLP" : "PEN"),
        estado: activo.estado || "activo",
        destino_tipo: activo.destino_tipo || "",
        destino_id: activo.destino_id || "",
        destino_empleado_id: activo.destino_empleado_id || "",
        numero_factura: activo.numero_factura || "",
        proyecto_nombre: activo.proyecto_nombre || "",
        fecha_planificada: activo.fecha_planificada || null,
        fecha_activacion: activo.fecha_activacion || null,
        categoria_contable: activo.categoria_contable || "",
        proveedor_id: activo.proveedor_id || null,
        factura_sii_recibida_id: activo.factura_sii_recibida_id || null,
        metodo_depreciacion: activo.metodo_depreciacion || "lineal",
      });
      setEditingId(activo.id);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Si cambia destino_tipo, resetear ambos ids de destino (integer y uuid empleado)
      if (field === "destino_tipo") {
        updated.destino_id = "";
        updated.destino_empleado_id = "";
      }

      return updated;
    });
  };

  const validateForm = () => {
    if (!formData.fecha_compra) {
      toast.error("Fecha de compra es requerida");
      return false;
    }
    if (!formData.tipo_activo) {
      toast.error("Tipo de activo es requerido");
      return false;
    }
    if (!formData.descripcion.trim()) {
      toast.error("Descripción es requerida");
      return false;
    }
    if (!formData.cantidad || formData.cantidad < 1) {
      toast.error("Cantidad debe ser al menos 1");
      return false;
    }
    if (!formData.costo_unitario || parseFloat(formData.costo_unitario) <= 0) {
      toast.error("Costo unitario debe ser mayor a 0");
      return false;
    }
    if (!formData.moneda) {
      toast.error("Moneda es requerida");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const vidaUtil = getVidaUtilPorTipo(formData.tipo_activo);

    const dataToSave = {
      fecha_compra: formatFechaToISO(formData.fecha_compra),
      tipo_activo: formData.tipo_activo,
      descripcion: formatearTexto(formData.descripcion),
      numero_serie: formData.numero_serie.trim() || null,
      cantidad: parseInt(formData.cantidad),
      costo_unitario: parseFloat(formData.costo_unitario),
      moneda: formData.moneda,
      vida_util_meses: vidaUtil,
      valor_residual: 0,
      estado: formData.estado,
      destino_tipo: formData.destino_tipo || null,
      // Empleado → destino_empleado_id (uuid); edificio/cartelería → destino_id (integer).
      destino_id: formData.destino_tipo === "empleado" || !formData.destino_id ? null : parseInt(formData.destino_id),
      destino_empleado_id: formData.destino_tipo === "empleado" ? formData.destino_empleado_id || null : null,
      numero_factura: formData.numero_factura.trim() || null,
      pais: normalizedPais,
      proyecto_nombre: formData.proyecto_nombre?.trim() || null,
      fecha_planificada: formData.fecha_planificada || null,
      fecha_activacion: formData.fecha_activacion || null,
      categoria_contable: formData.categoria_contable || null,
      proveedor_id: formData.proveedor_id ? parseInt(formData.proveedor_id, 10) : null,
      metodo_depreciacion: formData.metodo_depreciacion || "lineal",
    };

    try {
      if (editingId) {
        await actualizarActivo(editingId, dataToSave);
        toast.success("Activo actualizado correctamente");
      } else {
        await crearActivo(dataToSave);
        toast.success("Activo registrado correctamente");
      }
      closeModal();
      fetchData();
    } catch (err) {
      console.error("Error guardando activo:", err);
      toast.error("Error al guardar: " + err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await eliminarActivo(deleteId);
      toast.success("Activo eliminado correctamente");
      setDeleteId(null);
      fetchData();
    } catch (err) {
      console.error("Error eliminando activo:", err);
      toast.error("Error al eliminar: " + err.message);
    }
  };

  // =====================================================
  // COMPUTED VALUES
  // =====================================================

  const monedas = MONEDAS_POR_PAIS[normalizedPais] || ["USD"];

  const destinoOptions = useMemo(() => {
    if (formData.destino_tipo === "edificio") {
      return Object.entries(edificios).map(([id, nombre]) => ({ value: id, label: nombre }));
    }
    if (formData.destino_tipo === "carteleria") {
      return Object.entries(proyectosCarteleria).map(([id, nombre]) => ({ value: id, label: nombre }));
    }
    if (formData.destino_tipo === "empleado") {
      return Object.entries(empleados).map(([id, nombre]) => ({ value: id, label: nombre }));
    }
    return [];
  }, [formData.destino_tipo, edificios, proyectosCarteleria, empleados]);

  const vidaUtilActual = getVidaUtilPorTipo(formData.tipo_activo);

  const getDestinoNombre = (activo) => {
    if (!activo.destino_tipo) return "-";
    if (activo.destino_tipo === "edificio") {
      return edificios[activo.destino_id] || `Edificio #${activo.destino_id}`;
    }
    if (activo.destino_tipo === "carteleria") {
      return proyectosCarteleria[activo.destino_id] || `Proyecto #${activo.destino_id}`;
    }
    if (activo.destino_tipo === "empleado") {
      return empleados[activo.destino_empleado_id] || "Empleado";
    }
    return "-";
  };

  const getTipoLabel = (value) => {
    const tipo = TIPOS_ACTIVO.find((t) => t.value === value);
    if (tipo) return tipo.label;
    return value ? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : value;
  };

  const getEstadoLabel = (value) => {
    const estado = ESTADOS_ACTIVO.find((e) => e.value === value);
    return estado ? estado.label : value;
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (error) return <div className={styles.wrapper}>{error}</div>;
  if (loading || loadingRates) return <div className={styles.wrapper}>Cargando activos...</div>;

  return (
    <div className={styles.wrapper}>
      <Header leftContent={<h2 className={styles.titulo}>Gestión de Activos</h2>}>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FaPlus /> Agregar Activo
        </button>
      </Header>

      <div className={styles.content}>
        {activos.length === 0 ? (
          <div className={styles.empty}>No se encontraron activos registrados para {normalizedPais}</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.activosTable}>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>Cantidad</th>
                  <th>Costo Unitario</th>
                  <th>Costo Total</th>
                  <th>Vida Útil</th>
                  <th>Depreciación Amortización</th>
                  <th>Estado</th>
                  <th>Destino</th>
                  <th>Valor Libros (Hoy)</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {activos.map((activo) => {
                  const costoTotal = activo.costo_unitario * activo.cantidad;
                  const deprec = calcularDepreciacion(costoTotal, activo.vida_util_meses, activo.fecha_compra);
                  const esIntangible = esIntangiblePorTipo(activo.tipo_activo);

                  return (
                    <tr key={activo.id}>
                      <td>
                        {activo.fecha_compra ? parseFechaLocal(activo.fecha_compra).toLocaleDateString("es-CL") : "-"}
                      </td>
                      <td>{getTipoLabel(activo.tipo_activo)}</td>
                      <td title={`${activo.descripcion}${activo.numero_serie ? ` — S/N: ${activo.numero_serie}` : ""}`}>
                        {activo.descripcion}
                      </td>
                      <td>{activo.cantidad}</td>
                      <td>{formatAmount(activo.costo_unitario, activo.moneda)}</td>
                      <td>{formatAmount(costoTotal, activo.moneda)}</td>
                      <td>{activo.vida_util_meses} meses</td>
                      <td className={styles.deprecColumn}>
                        <span className={styles.deprecLabel}>{esIntangible ? "Amortización" : "Depreciación"}</span>
                        {formatAmount(deprec.depreciacionAcumulada, activo.moneda)}
                        <span className={styles.deprecPercent}>({deprec.porcentajeDepreciado.toFixed(0)}%)</span>
                      </td>
                      <td>
                        <span className={`${styles.estadoBadge} ${styles[activo.estado]}`}>
                          {getEstadoLabel(activo.estado)}
                        </span>
                      </td>
                      <td title={getDestinoNombre(activo)}>{getDestinoNombre(activo)}</td>
                      <td className={deprec.valorLibros === 0 ? styles.valorCero : ""}>
                        {formatAmount(deprec.valorLibros, activo.moneda)}
                      </td>
                      <td>
                        <button
                          className={`${styles.actionBtn} ${styles.editBtn}`}
                          onClick={() => openModal(activo)}
                          title="Editar"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className={`${styles.actionBtn} ${styles.deleteBtn}`}
                          onClick={() => setDeleteId(activo.id)}
                          title="Eliminar"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de formulario */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>{editingId ? "Editar Activo" : "Nuevo Activo"}</h5>
              <button type="button" className="btn-close" onClick={closeModal} />
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGrid}>
                {/* Fecha de compra */}
                <div className={styles.formGroup}>
                  <label>Fecha de compra *</label>
                  <DatePicker
                    selected={formData.fecha_compra}
                    onChange={(date) => handleInputChange("fecha_compra", date)}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText="dd/mm/aaaa"
                    minDate={new Date(2023, 0, 1)}
                    maxDate={new Date()}
                  />
                </div>

                {/* Tipo de activo */}
                <div className={styles.formGroup}>
                  <label>Tipo de activo *</label>
                  <select
                    className="form-select"
                    value={formData.tipo_activo}
                    onChange={(e) => handleInputChange("tipo_activo", e.target.value)}
                  >
                    <option value="">Seleccionar...</option>
                    {TIPOS_ACTIVO.map((tipo) => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Descripción */}
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Descripción *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange("descripcion", e.target.value)}
                    placeholder="Ej: Pantalla LED 55 Samsung"
                  />
                </div>

                {/* Número de serie */}
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Número(s) de serie</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.numero_serie}
                    onChange={(e) => handleInputChange("numero_serie", e.target.value)}
                    placeholder="Si son múltiples, separar por coma"
                  />
                </div>

                {/* Número de factura */}
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Número de factura</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.numero_factura}
                    onChange={(e) => handleInputChange("numero_factura", e.target.value)}
                    placeholder="Ej: FAC-2024-001"
                  />
                </div>

                {/* Cantidad */}
                <div className={styles.formGroup}>
                  <label>Cantidad *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.cantidad}
                    onChange={(e) => handleInputChange("cantidad", e.target.value)}
                    min="1"
                  />
                </div>

                {/* Costo unitario */}
                <div className={styles.formGroup}>
                  <label>Costo unitario *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.costo_unitario}
                    onChange={(e) => handleInputChange("costo_unitario", e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* Moneda */}
                <div className={styles.formGroup}>
                  <label>Moneda *</label>
                  <select
                    className="form-select"
                    value={formData.moneda}
                    onChange={(e) => handleInputChange("moneda", e.target.value)}
                  >
                    {monedas.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vida útil (solo lectura) */}
                <div className={styles.formGroup}>
                  <label>Vida útil</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.tipo_activo ? `${vidaUtilActual} meses` : "-"}
                    disabled
                  />
                </div>

                {/* Estado */}
                <div className={styles.formGroup}>
                  <label>Estado</label>
                  <select
                    className="form-select"
                    value={formData.estado}
                    onChange={(e) => handleInputChange("estado", e.target.value)}
                  >
                    {ESTADOS_ACTIVO.map((estado) => (
                      <option key={estado.value} value={estado.value}>
                        {estado.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Destino tipo */}
                <div className={styles.formGroup}>
                  <label>Destino</label>
                  <select
                    className="form-select"
                    value={formData.destino_tipo}
                    onChange={(e) => handleInputChange("destino_tipo", e.target.value)}
                  >
                    {DESTINO_TIPOS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Destino ID */}
                {formData.destino_tipo && (
                  <div className={styles.formGroup}>
                    <label>{DESTINO_TIPOS.find((d) => d.value === formData.destino_tipo)?.label || "Destino"}</label>
                    <select
                      className="form-select"
                      value={formData.destino_tipo === "empleado" ? formData.destino_empleado_id : formData.destino_id}
                      onChange={(e) =>
                        handleInputChange(
                          formData.destino_tipo === "empleado" ? "destino_empleado_id" : "destino_id",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Seleccionar...</option>
                      {destinoOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Proveedor vinculado */}
                <div className={styles.formGroup}>
                  <label>Proveedor (vinculado)</label>
                  <select
                    className="form-select"
                    value={formData.proveedor_id || ""}
                    onChange={(e) => handleInputChange("proveedor_id", e.target.value || null)}
                  >
                    <option value="">Sin vincular</option>
                    {proveedores.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Proyecto */}
                <div className={styles.formGroup}>
                  <label>Proyecto</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.proyecto_nombre}
                    onChange={(e) => handleInputChange("proyecto_nombre", e.target.value)}
                    placeholder="ej: Capital Golf - Ascensores"
                  />
                </div>

                {/* Categoría contable */}
                <div className={styles.formGroup}>
                  <label>Categoría contable</label>
                  <select
                    className="form-select"
                    value={formData.categoria_contable}
                    onChange={(e) => handleInputChange("categoria_contable", e.target.value)}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="hardware">Hardware</option>
                    <option value="software">Software</option>
                    <option value="infraestructura">Infraestructura</option>
                    <option value="investigacion">Investigación</option>
                    <option value="instalacion">Instalación</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                {/* Fecha planificada (solo cuando el estado es "planificado") */}
                {formData.estado === "planificado" && (
                  <div className={styles.formGroup}>
                    <label>Fecha planificada</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.fecha_planificada || ""}
                      onChange={(e) => handleInputChange("fecha_planificada", e.target.value)}
                    />
                  </div>
                )}

                {/* Fecha activación */}
                <div className={styles.formGroup}>
                  <label>Fecha de activación</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.fecha_activacion || ""}
                    onChange={(e) => handleInputChange("fecha_activacion", e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSubmit}>
                {editingId ? "Actualizar" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {deleteId && (
        <div className={styles.modalOverlay} onClick={() => setDeleteId(null)}>
          <div className={styles.modalContentSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Confirmar eliminación</h5>
              <button type="button" className="btn-close" onClick={() => setDeleteId(null)} />
            </div>
            <div className={styles.modalBody}>
              <p>¿Estás seguro de que deseas eliminar este activo?</p>
              <p className="text-danger">Esta acción no se puede deshacer.</p>
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionActivos;
